import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material'
import { DeleteActionButton, EditActionButton, ViewActionButton } from '../../../components/MRTDataGrid/TableActionButtons'
import { MRT_ColumnDef as MRTColumnDef } from 'material-react-table'
import { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useLoggedInUser } from '../../../app/hooks/useLoggedInUser'
import {
  useDeleteProductMutation,
  useGetProductCategoriesQuery,
  useGetProductsQuery,
} from '../../../app/redux-fetch/apiProducts'
import { useGetCustomersQuery } from '../../../app/redux-fetch/apiQuery'
import { IDropDownItem } from '../../../app/types'
import AddButton from '../../../components/Buttons/AddButton'
import FormDropdown from '../../../components/FormDropDown/FormDropdown'
import MRTDataGridv2 from '../../../components/MRTDataGrid/MRTDataGridv2'
import YesNoTypography from '../../../components/YesNoTypograpphy/YesNoTypography'
import commonMessages from '../../../messages'
import SideModalWrapper from '../../Assets/modals/SideModalWrapper'
import { useProductTabContext } from '../ProductTabContext'
import { IProduct } from '../types'
import CreateProductModal from './CreateProductModal'
import ProductModal from './ProductModal'
import messages from './messages'

const ProductTable = () => {
  const { formatMessage } = useIntl()
  const { tenantId, isCustomerUser } = useLoggedInUser()
  const { customerId, setCustomerId } = useProductTabContext()

  const { data: products, isSuccess: productLoaded } = useGetProductsQuery(
    { tenantId, customerId },
    {
      skip: customerId === undefined || customerId < 1 || tenantId === undefined || tenantId < 1,
    }
  )

  const { data: productCategories, isSuccess: productCategoriesLoaded } = useGetProductCategoriesQuery(
    { tenantId, customerId },
    {
      skip: customerId === undefined || customerId < 1 || tenantId === undefined || tenantId < 1,
    }
  )

  const { data: customers } = useGetCustomersQuery(tenantId ?? -1, {
    skip: tenantId === undefined || tenantId < 1,
  })

  const [deleteProduct, { isLoading: isDeleting }] = useDeleteProductMutation()

  const [createOpen, setCreateOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [viewOpen, setViewOpen] = useState(false)
  const [selectedProductId, setSelectedProductId] = useState(-1)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const handleDeleteProduct = async () => {
    try {
      setDeleteError(null)
      await deleteProduct({ tenantId, customerId, productId: selectedProductId }).unwrap()
      setDeleteOpen(false)
    } catch (error) {
      setDeleteError(formatMessage(messages.deleteProductError))
    }
  }

  const columns = useMemo<MRTColumnDef<IProduct>[]>(() => {
    return [
      {
        accessorKey: 'name',
        Cell: ({ renderedCellValue }: any) => {
          return <Typography sx={{ fontWeight: 700 }}>{renderedCellValue}</Typography>
        },
        header: formatMessage(messages.productName),
      },
      {
        accessorKey: 'sku',
        header: formatMessage(messages.productCode),
      },
      {
        accessorKey: 'barcode',
        header: formatMessage(messages.barcode),
      },
      {
        accessorFn: (row) => {
          if (!row.productCategoryId || !productCategories) return '';

          const category = productCategories.find(
            (productCategory: any) => productCategory.id === row.productCategoryId
          );

          return category?.name ?? '';
        },
        Cell: ({ renderedCellValue }: any) => {
          return <Typography>{renderedCellValue}</Typography>
        },
        header: formatMessage(messages.productCategory),
      },
      {
        accessorFn: (row) => {
          return row.countryOfOrigin?.name ?? ''
        },
        header: formatMessage(messages.countryOfOrigin),
      },
      {
        accessorFn: (row) => {
          return row.enabled ? 'true' : 'false'
        },
        filterVariant: 'checkbox',
        header: formatMessage(messages.enabled),
        muiTableHeadCellProps: {
          align: 'center',
        },
        muiTableBodyCellProps: {
          align: 'center',
        },
        Cell: ({ renderedCellValue }: any) => {
          return <YesNoTypography isYes={renderedCellValue === 'true'} />
        },
      },
      {
        accessorFn: (row) => {
          return row.inUse ? 'true' : 'false'
        },
        filterVariant: 'checkbox',
        header: formatMessage(messages.inUse),
        muiTableHeadCellProps: {
          align: 'center',
        },
        muiTableBodyCellProps: {
          align: 'center',
        },
        Cell: ({ renderedCellValue }: any) => {
          return <YesNoTypography isYes={renderedCellValue === 'true'} />
        },
      },
    ]
  }, [productCategories, formatMessage])

  const isLoading = useMemo(
    () => customerId > 0 && (!productLoaded || !productCategoriesLoaded),
    [productLoaded, productCategoriesLoaded, customerId]
  )

  return (
    <>
      <SideModalWrapper
        open={createOpen}
        key={'create'}
        headerLabel={formatMessage(messages.createEditProduct)}
        onClose={() => {
          setCreateOpen((pv) => {
            return !pv
          })
        }}
        withBottomBox={false}
      >
        {createOpen && <CreateProductModal />}
      </SideModalWrapper>
      <SideModalWrapper
        open={editOpen}
        key={'edit'}
        headerLabel={formatMessage(messages.createEditProduct)}
        onClose={() => {
          setEditOpen((pv) => {
            return !pv
          })
        }}
        withBottomBox={false}
      >
        {editOpen && <ProductModal productId={selectedProductId} customerId={customerId ?? -1} />}
      </SideModalWrapper>
      <SideModalWrapper
        open={viewOpen}
        key={'view'}
        headerLabel={formatMessage(messages.viewProduct)}
        onClose={() => {
          setViewOpen((pv) => {
            return !pv
          })
        }}
        withBottomBox={false}
      >
        {viewOpen && <ProductModal
          productId={selectedProductId}
          customerId={customerId ?? -1}
          isDisabled={true}
        />}
      </SideModalWrapper>
      <Dialog
        open={deleteOpen}
        onClose={() => {
          setDeleteOpen(false)
          setDeleteError(null)
        }}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
      >
        <DialogTitle id='alert-dialog-title'>
          {formatMessage(messages.deleteProductTitle)}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id='alert-dialog-description'>
            {formatMessage(messages.deleteProductDescription)}
          </DialogContentText>
          {deleteError && (
            <DialogContentText color='error' sx={{ mt: 2 }}>
              {deleteError}
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteOpen(false)
              setDeleteError(null)
            }}
            color='primary'
          >
            {formatMessage(commonMessages.cancel)}
          </Button>
          <Button
            onClick={handleDeleteProduct}
            color='primary'
            disabled={isDeleting}
          >
            {formatMessage(commonMessages.delete)}
          </Button>
        </DialogActions>
      </Dialog>
      <MRTDataGridv2
        initialState={{
          pagination: { pageSize: 100, pageIndex: 0 },
          density: 'comfortable',
          sorting: [
            {
              id: 'name',
              desc: false,
            },
          ],
        }}
        state={{
          isLoading: isLoading,
        }}
        leftHeadingComponent={
          isCustomerUser ? (
            <></>
          ) : (
            <>
              <FormDropdown
                id='customerId'
                items={customers as IDropDownItem[]}
                onChange={async (e: any, name: any, newValue: any) => {
                  setCustomerId && setCustomerId(newValue)
                }}
                sx={{ width: '400px', mt: '12px' }}
                value={customerId}
                label='Customer'
              />
            </>
          )
        }
        rightHeadingComponent={
          <AddButton
            text={formatMessage(messages.newProductBtn)}
            onClick={() => setCreateOpen(true)}
            disabled={!customerId || customerId < 1}
          />
        }
        columns={columns}
        data={products || []}
        getRowId={(row) => row.id as any}
        enableEditing
        enableRowActions
        positionActionsColumn='last'
        renderRowActions={({ row, table }: any) => {
          const productId = row.original.id
          const canDelete = !row.original.inUse

          return (
            <Box sx={{ display: 'flex' }}>
              <ViewActionButton
                onClick={() => {
                  setSelectedProductId(productId)
                  setViewOpen(true)
                }}
              />
              <EditActionButton
                onClick={() => {
                  setSelectedProductId(productId)
                  setEditOpen(true)
                }}
              />
              {canDelete && (
                <DeleteActionButton
                  onClick={() => {
                    setSelectedProductId(productId)
                    setDeleteOpen(true)
                  }}
                />
              )}
            </Box>
          )
        }}
      />
    </>
  )
}

export default ProductTable
